// src/pages/analytics/GpAnalysisTab.tsx
//
// GP Analysis — gross profit by dimension over a date range (recipe:
// gp-analysis). Agent 3.20.0+ costs each invoice line at its own cost at time
// of sale (costBasis 'line'); older agents estimate from CURRENT average cost
// ('average', column reads "Est Cost"). The server explains the basis in
// `note`, rendered as a muted line under the controls, plus an amber line
// when sales with no cost on file are material. Cube columns (Cube Ft / GP per Cube / GP per Pallet) render
// ONLY when the response says cubeEnabled.
//
// GP % thresholds: amber < 15, red < 8. Margin % / Markup % / Both toggle
// (./marginMode): markup = GP ÷ cost, coloured by the row's margin.
//
// Drill-down (flag analytics.drill, agent 3.20.0+): a group row expands into
// its items / customers — see ./drill.

import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Percent, RefreshCw, Download, FileSpreadsheet, RotateCcw, AlertTriangle, ChevronRight, ChevronDown } from 'lucide-react';
import { apiV2 as api } from '../../services/apiV2';
import DateField from '../../components/DateField';
import type { GpRow, GpAnalysisResponse } from '../../types';
import { exportCsv, type CsvColumns } from '../../lib/csvExport';
import { exportXlsx } from '../../lib/xlsxExport';
import { FilterPane, AnalyticsSavedViews } from './filterPane';
import { useFilterPane } from './filterPaneState';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { DRILL, DRILL_ROWS, canDrillLabel, drillParams } from './drill';
import { PctModeToggle, usePctMode, markupPct, showMargin, showMarkup, type PctMode } from './marginMode';

const fmtCur = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
const fmtCur2 = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
const fmtNum = (v: number) => new Intl.NumberFormat('en-US').format(Math.round(v));

const DIMENSIONS: { value: string; label: string }[] = [
  { value: 'item_category',     label: 'Item Category' },
  { value: 'item_subcategory',  label: 'Item Subcategory' },
  { value: 'customer_category', label: 'Customer Category' },
  { value: 'price_level',       label: 'Price Level' },
  { value: 'item',              label: 'Item' },
  { value: 'customer',          label: 'Customer' },
  { value: 'salesman',          label: 'Salesman' },
  { value: 'area',              label: 'Area' },
];

/** Local yyyy-MM-dd for `monthsAgo` months before today (0 = today). */
function isoDaysAgoMonths(monthsAgo: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - monthsAgo);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

const costLabel = (data?: GpAnalysisResponse) => data?.costBasis === 'line' ? 'Cost' : 'Est Cost';

function exportRows(data: GpAnalysisResponse, dimensionLabel: string, exporter: typeof exportCsv = exportCsv, mode: PctMode = 'margin') {
  const cols: CsvColumns<GpRow> = [
    [dimensionLabel, (r) => r.label],
    ['Qty',       (r) => r.qty],
    ...(data.totals.grossSales != null ? ([
      ['Gross',   (r: GpRow) => r.grossSales ?? ''],
      ['Returns', (r: GpRow) => r.returns ?? ''],
    ] as CsvColumns<GpRow>) : []),
    [data.totals.grossSales != null ? 'Net Sales' : 'Sales', (r) => r.sales],
    [costLabel(data), (r) => r.estCost],
    ['GP $',      (r) => r.gp],
    ...(showMargin(mode) ? ([['Margin %', (r: GpRow) => r.gpPct]] as CsvColumns<GpRow>) : []),
    ...(showMarkup(mode) ? ([['Markup %', (r: GpRow) => { const m = markupPct(r.gp, r.estCost); return m == null ? '' : Math.round(m * 10) / 10; }]] as CsvColumns<GpRow>) : []),
    ['GP/Unit',   (r) => r.gpPerUnit],
    ...(data.cubeEnabled ? ([
      ['Cube Ft',   (r: GpRow) => r.cubeFt ?? ''],
      ['GP/Cube',   (r: GpRow) => r.gpPerCube ?? ''],
      ['GP/Pallet', (r: GpRow) => r.gpPerPallet ?? ''],
    ] as CsvColumns<GpRow>) : []),
  ];
  exporter('gp-analysis', cols, [...data.rows, { ...data.totals, label: 'TOTAL' }]);
}

function GpPctCell({ pct, colorBy }: { pct: number | null; colorBy?: number }) {
  if (pct == null) return <span className="text-slate-600">–</span>;
  const c = colorBy ?? pct;
  const color = c < 8 ? 'text-red-400' : c < 15 ? 'text-amber-400' : 'text-emerald-400';
  return <span className={`tabular-nums font-medium ${color}`}>{pct.toFixed(1)}%</span>;
}

/** Margin % and/or markup % cells for one row, per the toggle. */
function PctCells({ r, mode }: { r: { gp: number; estCost: number; gpPct: number }; mode: PctMode }) {
  return (
    <>
      {showMargin(mode) && <td className="px-3 py-2 text-right"><GpPctCell pct={r.gpPct} /></td>}
      {showMarkup(mode) && <td className="px-3 py-2 text-right"><GpPctCell pct={markupPct(r.gp, r.estCost)} colorBy={r.gpPct} /></td>}
    </>
  );
}

/** The numeric cells of one GP row (everything after the label). */
function GpCells({ r, hasGross, cubeEnabled, mode }: { r: GpRow; hasGross: boolean; cubeEnabled: boolean; mode: PctMode }) {
  return (
    <>
      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtNum(r.qty)}</td>
      {hasGross && (
        <>
          <td className="px-3 py-2 text-right text-slate-400 tabular-nums">{fmtCur(r.grossSales ?? 0)}</td>
          <td className="px-3 py-2 text-right tabular-nums text-red-300/80">{r.returns ? `(${fmtCur(r.returns)})` : '–'}</td>
        </>
      )}
      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.sales)}</td>
      <td className="px-3 py-2 text-right text-slate-400 tabular-nums">{fmtCur(r.estCost)}</td>
      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur(r.gp)}</td>
      <PctCells r={r} mode={mode} />
      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur2(r.gpPerUnit)}</td>
      {cubeEnabled && (
        <>
          <td className="px-3 py-2 text-right text-slate-400 tabular-nums border-l border-slate-800/60">{r.cubeFt != null ? fmtNum(r.cubeFt) : '–'}</td>
          <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{r.gpPerCube != null ? fmtCur2(r.gpPerCube) : '–'}</td>
          <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{r.gpPerPallet != null ? fmtCur(r.gpPerPallet) : '–'}</td>
        </>
      )}
    </>
  );
}

/** Child rows under an expanded group row (top DRILL_ROWS by sales). */
function GpDrillRows({ dimension, label, dateFrom, dateTo, paneParams, paneKey, hasGross, cubeEnabled, mode }: {
  dimension: string; label: string; dateFrom: string; dateTo: string;
  paneParams: Record<string, string | undefined>; paneKey: string; hasGross: boolean; cubeEnabled: boolean; mode: PctMode;
}) {
  const d = DRILL[dimension];
  const q = useQuery({
    queryKey: ['analyticsGpDrill', dimension, label, dateFrom, dateTo, paneKey],
    queryFn: () => api.getGpAnalysis({ dimension: d.child, dateFrom, dateTo }, drillParams(paneParams, d, label)),
    staleTime: 60_000,
    retry: 1,
  });
  const span = 7 + (hasGross ? 2 : 0) + (cubeEnabled ? 3 : 0) + (mode === 'both' ? 1 : 0);
  if (q.isLoading || q.error) {
    return (
      <tr className="border-b border-slate-800/50 bg-slate-900/60">
        <td colSpan={span} className="pl-9 pr-3 py-2 text-slate-500 italic">
          {q.isLoading ? `Loading ${d.childLabel}…` : `Couldn't load ${d.childLabel}: ${(q.error as Error).message}`}
        </td>
      </tr>
    );
  }
  const kids = [...(q.data?.rows ?? [])].sort((a, b) => b.sales - a.sales);
  return (
    <>
      {kids.slice(0, DRILL_ROWS).map((r, i) => (
        <tr key={`drill-${r.label}-${i}`} className="group border-b border-slate-800/40 bg-slate-900/60 hover:bg-slate-700/20">
          <td className="sticky left-0 z-10 bg-slate-950 group-hover:bg-slate-800 pl-9 pr-3 py-1.5 text-slate-300 whitespace-nowrap max-w-[260px] truncate" title={r.label}>{r.label}</td>
          <GpCells r={r} hasGross={hasGross} cubeEnabled={cubeEnabled} mode={mode} />
        </tr>
      ))}
      {kids.length === 0 && (
        <tr className="border-b border-slate-800/50 bg-slate-900/60">
          <td colSpan={span} className="pl-9 pr-3 py-2 text-slate-500 italic">No {d.childLabel}</td>
        </tr>
      )}
      {kids.length > DRILL_ROWS && (
        <tr className="border-b border-slate-800/50 bg-slate-900/60">
          <td colSpan={span} className="pl-9 pr-3 py-1.5 text-[11px] text-slate-500">
            Top {DRILL_ROWS} of {kids.length} {d.childLabel} by sales
          </td>
        </tr>
      )}
    </>
  );
}

export function GpAnalysisTab() {
  const [dimension, setDimension] = useState('item_category');
  // Expanded group row label (drill-down); cleared when the dimension changes.
  const [expanded, setExpanded] = useState<string | null>(null);
  // Default window: trailing 12 months.
  const [dateFrom, setDateFrom] = useState(() => isoDaysAgoMonths(12));
  const [dateTo, setDateTo] = useState(() => isoDaysAgoMonths(0));
  const [pctMode, setPctMode] = usePctMode();
  const resetFilters = () => {
    setDimension('item_category');
    setDateFrom(isoDaysAgoMonths(12));
    setDateTo(isoDaysAgoMonths(0));
  };

  const pane = useFilterPane();
  const q = useQuery({
    queryKey: ['analyticsGp', dimension, dateFrom, dateTo, pane.key],
    queryFn: () => api.getGpAnalysis({ dimension, dateFrom, dateTo }, pane.params),
    staleTime: 60_000,
    retry: 1,
  });

  const data = q.data;
  const rows = data?.rows ?? [];
  const totals = data?.totals;
  const cubeEnabled = data?.cubeEnabled ?? false;
  // Gross / Returns columns appear once the agent sends them (3.20.0+).
  const hasGross = totals?.grossSales != null;
  const dimensionLabel = DIMENSIONS.find(d => d.value === dimension)?.label ?? dimension;
  const { canSee } = useFeatureAccess();
  // filtersActive marks an agent that honours the pane slicers (3.20.0+).
  const drill = canSee('analytics.drill') && data?.filtersActive != null ? DRILL[dimension] : undefined;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <Percent size={20} /> GP Analysis
        </h2>
        <p className="text-sm text-slate-400 mt-0.5">
          Estimated gross profit by {dimensionLabel.toLowerCase()}
          {data && <span className="text-slate-500"> · {data.dateFrom} → {data.dateTo}</span>}
        </p>
      </div>

      {/* Controls */}
      <div className="flex flex-wrap items-end gap-3 p-4 rounded-xl bg-slate-800/20 border border-slate-700/30">
        <div className="flex flex-col gap-1">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Dimension</span>
          <select value={dimension} onChange={e => { setDimension(e.target.value); setExpanded(null); }}
            className="px-2 py-1.5 rounded-md bg-slate-800/50 border border-slate-700/50 text-xs text-white focus:outline-none focus:ring-1 focus:ring-blue-500/50">
            {DIMENSIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
          </select>
        </div>
        <DateField label="From" value={dateFrom} onChange={setDateFrom} />
        <DateField label="To" value={dateTo} onChange={setDateTo} />
        <PctModeToggle mode={pctMode} onChange={setPctMode} />
        <div className="flex-1" />
        <AnalyticsSavedViews
          page="gp-analysis"
          getState={() => ({ dimension, dateFrom, dateTo })}
          applyState={(s) => {
            if (typeof s.dimension === 'string' && DIMENSIONS.some(d => d.value === s.dimension)) { setDimension(s.dimension); setExpanded(null); }
            if (typeof s.dateFrom === 'string') setDateFrom(s.dateFrom);
            if (typeof s.dateTo === 'string') setDateTo(s.dateTo);
          }}
        />
        <button onClick={resetFilters} title="Reset all filters to defaults"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1">
          <RotateCcw size={11} /> Reset
        </button>
        <button
          onClick={() => data && exportRows(data, dimensionLabel, exportCsv, pctMode)}
          disabled={!data || rows.length === 0}
          title="Export the GP table to CSV"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <Download size={11} /> CSV
        </button>
        <button
          onClick={() => data && exportRows(data, dimensionLabel, exportXlsx, pctMode)}
          disabled={!data || rows.length === 0}
          title="Export the GP table to Excel"
          className="px-2 py-1.5 text-[11px] rounded-md border border-slate-700/50 text-slate-300 hover:text-white hover:border-slate-500 bg-slate-900/40 flex items-center gap-1 disabled:opacity-40">
          <FileSpreadsheet size={11} /> Excel
        </button>
        <button onClick={() => q.refetch()} disabled={q.isLoading}
          className="p-2 rounded-md bg-slate-700/50 border border-slate-600/50 text-slate-400 hover:text-white transition-all disabled:opacity-50">
          <RefreshCw size={14} className={q.isFetching ? 'animate-spin' : ''} />
        </button>
      </div>

      {data?.note && (
        <p className="text-xs text-slate-500 italic px-1">{data.note}</p>
      )}
      {totals?.noCostSales != null && (totals.noCostPct ?? 0) >= 0.5 && (
        <p className="text-xs text-amber-400/90 px-1 flex items-center gap-1.5">
          <AlertTriangle size={12} />
          {fmtCur(totals.noCostSales)} ({(totals.noCostPct ?? 0).toFixed(1)}% of sales) is on items with no cost on file — counted at 100% GP, as Advantage does.
        </p>
      )}

      <FilterPane />

      {q.error && (
        <div className="p-4 rounded-md bg-red-950/30 border border-red-900/40 text-red-200 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          <div>
            <div className="font-medium">Couldn't load the GP analysis</div>
            <div className="text-xs text-red-300/70 mt-0.5">{(q.error as Error).message}</div>
          </div>
        </div>
      )}

      {q.isLoading ? (
        <div className="flex items-center justify-center h-40"><RefreshCw className="w-6 h-6 text-blue-400 animate-spin" /></div>
      ) : !q.error && data && (
        <>
          {/* Tiles */}
          {totals && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="rounded-xl border border-blue-500/30 bg-blue-500/5 p-4">
                <div className="text-xs text-slate-400 uppercase tracking-wider">Sales</div>
                <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur(totals.sales)}</div>
              </div>
              <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
                <div className="text-xs text-slate-400 uppercase tracking-wider">GP $</div>
                <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur(totals.gp)}</div>
              </div>
              <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4">
                <div className="text-xs text-slate-400 uppercase tracking-wider">{pctMode === 'markup' ? 'Markup %' : pctMode === 'both' ? 'Margin / Markup' : 'Margin %'}</div>
                <div className="text-xl font-semibold tabular-nums mt-1">
                  {showMargin(pctMode) && <GpPctCell pct={totals.gpPct} />}
                  {pctMode === 'both' && <span className="text-slate-500 mx-1.5">/</span>}
                  {showMarkup(pctMode) && <GpPctCell pct={markupPct(totals.gp, totals.estCost)} colorBy={totals.gpPct} />}
                </div>
              </div>
              <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4">
                <div className="text-xs text-slate-400 uppercase tracking-wider">GP / Unit</div>
                <div className="text-xl font-semibold text-white tabular-nums mt-1">{fmtCur2(totals.gpPerUnit)}</div>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-700/50 bg-slate-800/50">
                    <th className="sticky left-0 z-10 bg-slate-800 px-3 py-2.5 text-left text-slate-400 font-medium min-w-[160px]">{dimensionLabel}</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Qty</th>
                    {hasGross && (
                      <>
                        <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Gross</th>
                        <th className="px-3 py-2.5 text-right text-slate-400 font-medium">Returns</th>
                      </>
                    )}
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">{hasGross ? 'Net Sales' : 'Sales'}</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">{costLabel(data)}</th>
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">GP $</th>
                    {showMargin(pctMode) && <th className="px-3 py-2.5 text-right text-slate-400 font-medium" title="GP ÷ sales">Margin %</th>}
                    {showMarkup(pctMode) && <th className="px-3 py-2.5 text-right text-slate-400 font-medium" title="GP ÷ cost">Markup %</th>}
                    <th className="px-3 py-2.5 text-right text-slate-400 font-medium">GP/Unit</th>
                    {cubeEnabled && (
                      <>
                        <th className="px-3 py-2.5 text-right text-slate-400 font-medium border-l border-slate-700/40">Cube Ft</th>
                        <th className="px-3 py-2.5 text-right text-slate-400 font-medium">GP/Cube</th>
                        <th className="px-3 py-2.5 text-right text-slate-400 font-medium">GP/Pallet</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => {
                    const canExpand = !!drill && canDrillLabel(r.label);
                    const isOpen = canExpand && expanded === r.label;
                    return (
                      <Fragment key={`${r.label}-${i}`}>
                        <tr
                          onClick={canExpand ? () => setExpanded(isOpen ? null : r.label) : undefined}
                          title={canExpand ? `${isOpen ? 'Collapse' : 'Show'} ${drill!.childLabel}` : undefined}
                          className={`group border-b border-slate-800/50 hover:bg-slate-700/20 transition-colors${canExpand ? ' cursor-pointer' : ''}`}>
                          <td className="sticky left-0 z-10 bg-slate-900 group-hover:bg-slate-800 px-3 py-2 text-white whitespace-nowrap max-w-[260px] truncate" title={r.label}>
                            {drill && (
                              <span className="inline-block w-4 text-slate-500 align-middle">
                                {canExpand && (isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />)}
                              </span>
                            )}
                            {r.label}
                          </td>
                          <GpCells r={r} hasGross={hasGross} cubeEnabled={cubeEnabled} mode={pctMode} />
                        </tr>
                        {isOpen && (
                          <GpDrillRows dimension={dimension} label={r.label} dateFrom={dateFrom} dateTo={dateTo}
                            paneParams={pane.params} paneKey={pane.key} hasGross={hasGross} cubeEnabled={cubeEnabled} mode={pctMode} />
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
                {totals && rows.length > 0 && (
                  <tfoot>
                    <tr className="border-t-2 border-slate-600/60 bg-slate-800/60 font-semibold">
                      <td className="sticky left-0 z-10 bg-slate-800 px-3 py-2 text-white">Total</td>
                      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtNum(totals.qty)}</td>
                      {hasGross && (
                        <>
                          <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(totals.grossSales ?? 0)}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-red-300">{totals.returns ? `(${fmtCur(totals.returns)})` : '–'}</td>
                        </>
                      )}
                      <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.sales)}</td>
                      <td className="px-3 py-2 text-right text-slate-300 tabular-nums">{fmtCur(totals.estCost)}</td>
                      <td className="px-3 py-2 text-right text-white tabular-nums">{fmtCur(totals.gp)}</td>
                      <PctCells r={totals} mode={pctMode} />
                      <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{fmtCur2(totals.gpPerUnit)}</td>
                      {cubeEnabled && (
                        <>
                          <td className="px-3 py-2 text-right text-slate-300 tabular-nums border-l border-slate-700/60">{totals.cubeFt != null ? fmtNum(totals.cubeFt) : '–'}</td>
                          <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{totals.gpPerCube != null ? fmtCur2(totals.gpPerCube) : '–'}</td>
                          <td className="px-3 py-2 text-right text-slate-200 tabular-nums">{totals.gpPerPallet != null ? fmtCur(totals.gpPerPallet) : '–'}</td>
                        </>
                      )}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            {rows.length === 0 && (
              <div className="text-center py-8 text-slate-500 text-sm">No rows to show</div>
            )}
            <div className="px-3 py-2 border-t border-slate-700/30 text-xs text-slate-500">
              {rows.length} row{rows.length === 1 ? '' : 's'}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
